import { EmptyState } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { PERSONA_OPPORTUNITY_SET_GRID_CLASS } from "../../../lib/persona-opportunity-grid-classes.js";
import { CreatePersonaOpportunitySetCard } from "../../sets/CreatePersonaOpportunitySetCard/CreatePersonaOpportunitySetCard.js";

export interface PersonaOpportunitiesEmptyStateProps {
  onCreateSet?: () => void;
  canCreateSet?: boolean;
  isCreatingSet?: boolean;
}

/**
 * Zero-sets surface.
 * - AE1: `canCreateSet` → dashed onboarding card.
 * - AE8: list-only → read-only empty copy, no dashed card.
 */
export function PersonaOpportunitiesEmptyState({
  onCreateSet,
  canCreateSet = true,
  isCreatingSet,
}: PersonaOpportunitiesEmptyStateProps) {
  const { t } = useTranslation();

  if (!canCreateSet) {
    return (
      <EmptyState
        title={t(
          "personaOpportunities.readOnlyEmptyTitle",
          "No opportunity sets yet",
        )}
        description={t(
          "personaOpportunities.readOnlyEmptyDescription",
          "Ask someone with create access to generate the first set for this tenant.",
        )}
      />
    );
  }

  return (
    <section className="flex min-w-0 flex-col gap-3.5 overflow-x-visible">
      <h2 className="text-[15px] font-semibold leading-tight tracking-tight text-foreground">
        {t("personaOpportunities.yourSets")}
      </h2>
      <div className={PERSONA_OPPORTUNITY_SET_GRID_CLASS}>
        <CreatePersonaOpportunitySetCard
          onClick={onCreateSet}
          canCreate={canCreateSet}
          isCreatingSet={isCreatingSet}
          showAction={!!onCreateSet}
        />
      </div>
    </section>
  );
}
